import { useEffect, useRef, useState } from "react";
import { Copy, Plus, Send, X } from "lucide-react";
import { AUTHOR_QUESTION_MAX_CHARS, MAX_AUTHOR_QUESTIONS } from "@ttc-spectree/toolbox-contract";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The questions for an item's author as an editable list (epic
 * PI-SPEC-SW-E292, feature PI-SPEC-SW-1498, UX-PM-06, frame D3): the poster
 * rewords, removes or adds a question (at most {@link MAX_AUTHOR_QUESTIONS},
 * each at most {@link AUTHOR_QUESTION_MAX_CHARS} characters) and posts exactly
 * what is in the boxes, trimmed, blanks dropped. No confirm dialog.
 *
 * Every question starts as Claude's draft: untrusted model output, so it is
 * only ever a textarea value, never HTML. The edits live in this component's
 * state: the card is keyed by item, so leaving the item keeps Claude's draft,
 * not the edits. The caller keys this editor by its starting questions, so a
 * new draft starts the boxes over.
 */

/** What a post sends: every box trimmed, blank boxes dropped. Pure. */
export function questionsToPost(boxes: readonly string[]): string[] {
  return boxes.map((box) => box.trim()).filter((box) => box.length > 0);
}

/** "Post 3 questions", "Post 1 question". Pure. */
export function postButtonLabel(count: number): string {
  return `Post ${String(count)} ${count === 1 ? "question" : "questions"}`;
}

export interface AuthorQuestionsEditorProps {
  /** The starting questions (Claude's newest draft). */
  initial: readonly string[];
  /** The item creator's name when ADO can @mention them; null when it cannot. */
  authorName: string | null;
  /** The signed-in person's name; the comment is posted as them. */
  posterName: string | null;
  /** True while the post is in flight. */
  pending: boolean;
  onPost: (questions: string[]) => void;
  onCopy: (questions: string[]) => void;
  /** Focus the first box when the editor opens (Ask again, or arriving from the Backlog's Ask the author). */
  autoFocus?: boolean;
}

export function AuthorQuestionsEditor({
  initial,
  authorName,
  posterName,
  pending,
  onPost,
  onCopy,
  autoFocus = false,
}: AuthorQuestionsEditorProps) {
  const [boxes, setBoxes] = useState<string[]>(() =>
    initial.slice(0, MAX_AUTHOR_QUESTIONS).map((question) => question.slice(0, AUTHOR_QUESTION_MAX_CHARS))
  );
  const listRef = useRef<HTMLOListElement>(null);
  const focusIndex = useRef<number | null>(autoFocus ? 0 : null);

  useEffect(() => {
    const index = focusIndex.current;
    if (index === null) return;
    focusIndex.current = null;
    listRef.current?.querySelectorAll("textarea")[index]?.focus();
  }, [boxes.length]);

  const toPost = questionsToPost(boxes);
  const count = toPost.length;
  const canAdd = boxes.length < MAX_AUTHOR_QUESTIONS;
  const you = posterName ? `posted as you (${posterName})` : "posted as you";

  return (
    <div className="flex flex-col gap-2.5" data-testid="author-questions-editor">
      <ol ref={listRef} className="flex flex-col gap-2" data-testid="author-question-boxes">
        {boxes.map((box, index) => (
          <li key={index} className="flex items-start gap-2" data-testid="author-question-box">
            <span className={cn("w-3.5 flex-none pt-[9px] text-right text-xs", TOOLBOX_TEXT.faint)} aria-hidden="true">
              {index + 1}
            </span>
            <textarea
              className="min-h-[38px] w-full flex-1 resize-none rounded-lg border border-[#27272A] bg-[#0B0B0D] px-2.5 py-2 text-[13.5px] leading-[1.45] text-foreground [field-sizing:content] focus:border-[#23A4FF] focus:outline-none focus:ring-[3px] focus:ring-[rgba(35,164,255,0.18)]"
              aria-label={`Question ${String(index + 1)}`}
              value={box}
              maxLength={AUTHOR_QUESTION_MAX_CHARS}
              rows={1}
              onChange={(event) => {
                const value = event.target.value;
                setBoxes((current) => current.map((old, at) => (at === index ? value : old)));
              }}
            />
            <button
              type="button"
              className={cn(
                "mt-1 inline-flex h-[30px] w-[26px] flex-none items-center justify-center rounded-md hover:bg-[#141417] hover:text-foreground",
                TOOLBOX_TEXT.faint
              )}
              aria-label={`Remove question ${String(index + 1)}`}
              title="Remove"
              onClick={() => {
                setBoxes((current) => current.filter((_, at) => at !== index));
              }}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
      {canAdd && (
        <button
          type="button"
          className="inline-flex items-center gap-1 self-start pl-[22px] text-xs text-[#6CC4FF] hover:underline"
          data-testid="add-author-question"
          onClick={() => {
            focusIndex.current = boxes.length;
            setBoxes((current) => [...current, ""]);
          }}
        >
          <Plus className="h-3 w-3" aria-hidden="true" />
          Add a question
        </button>
      )}

      <div className="flex flex-col gap-2.5 border-t border-[#1F1F23] pt-3">
        <p className="text-xs leading-normal text-muted-foreground" data-testid="author-questions-mention-line">
          {authorName ? (
            <>
              One comment, {you}. It names <b className="font-semibold text-foreground">@{authorName}</b>, so ADO emails
              them.
            </>
          ) : (
            <>One comment, {you}. ADO cannot name this item&apos;s author, so tell them yourself.</>
          )}
        </p>
        <div className="flex items-center gap-2">
          <span className="flex-1" />
          <button
            type="button"
            className="inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1E] px-2.5 text-[13px] font-medium hover:bg-[#222227]"
            onClick={() => {
              onCopy(toPost);
            }}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            Copy
          </button>
          <button
            type="button"
            className="inline-flex h-[30px] items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            disabled={count === 0 || pending}
            data-testid="post-author-questions"
            onClick={() => {
              onPost(toPost);
            }}
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            {pending ? "Posting…" : postButtonLabel(count)}
          </button>
        </div>
      </div>
    </div>
  );
}
